import { Link, useParams } from 'react-router-dom';

import { PageHeader } from '../../../components/PageHeader';
import { invariant } from '../../../utils/invariant';
import { useQuotationRequest } from '../models';
import { DataField } from '../../../components/DataField';
import { formatDate } from '../../../utils/date';
import { Breadcrumb } from '../../../components/Breadcrumb';
import { TrailerTypeIcon } from '../../order/pages/order/TrailerTypes';
import { LinkButton } from '../../../components/button/ButtonLink';
import { PageHeading } from 'components/PageHeading';
import { SummaryBar } from 'components/SummaryBar';
import { SpinnerBlock } from '../../../components/Spinner';
import { EmptyState } from 'components/empty-state/EmptyState';
import { CustomError, ErrorCode } from 'src/error';

export const QuotationRequestPage = () => {
  const { quotationRequestId } = useParams<{ quotationRequestId: string }>();
  invariant(quotationRequestId);

  const { data: quotation, error, isLoading } = useQuotationRequest(quotationRequestId);

  if (isLoading) {
    return <SpinnerBlock message="Offerte aanvraag laden..." />;
  }

  if (error || !quotation) {
    const isNotFound = (error instanceof CustomError && error.code === ErrorCode.NOT_FOUND) || (!error && !quotation);

    return (
      <EmptyState
        title={isNotFound ? 'Offerte aanvraag niet gevonden' : 'Offerte aanvraag kon niet worden geladen'}
        description={isNotFound ? undefined : (error as Error | undefined)?.message}
      />
    );
  }

  const title = `Offerte aanvraag ${quotation.reference}`;

  return (
    <>
      <PageHeader title={title} />

      <PageHeading
        noMarginBottom
        leftSide={
          <Breadcrumb
            parentItem={{
              name: 'Offerte aanvragen',
              to: '..',
            }}
            currentItem={title}
          />
        }
        rightSide={
          <div>
            <LinkButton to={`/internal/finance/quotations/new?quotation-req-id=${quotationRequestId}`}>
              Maak offerte aan
            </LinkButton>
          </div>
        }
      />

      <div className="flex flex-col gap-4 p-7">
        <SummaryBar>
          <DataField title="Type">{quotation.type}</DataField>
          <DataField title="Referentie">{quotation.reference}</DataField>
          <DataField title="Aangevraagd op">{formatDate(quotation.createdAt)}</DataField>
          <DataField title="Offerte">
            {quotation.quotationId ? (
              <Link to={`../../quotations/${quotation.quotationId}`} className="link-text">
                {quotation.reference}
              </Link>
            ) : (
              'n/a'
            )}
          </DataField>

          {quotation.notes.length > 0 && (
            <DataField className="w-full" title="Notities">
              <span className="whitespace-pre-wrap">{quotation.notes}</span>
            </DataField>
          )}
        </SummaryBar>

        <div className="card">
          <div className="grid grid-cols-8 border-b border-border bg-surface-sunken px-5 py-2.5 text-sm font-semibold text-fg">
            <div className="col-span-3">Vertrek</div>
            <div className="col-span-3">Bestemming</div>
          </div>

          {quotation.lines.map((line) => {
            return (
              <div className="flex flex-col gap-2.5 border-b border-border px-5 py-3.5 last:border-b-0" key={line.id}>
                <div className="grid grid-cols-8 text-sm">
                  <div className="col-span-3">{`${line.departureCountry}${line.departurePostalCode} ${line.departureCity}`}</div>
                  <div className="col-span-3">{`${line.arrivalCountry}${line.arrivalPostalCode} ${line.arrivalCity}`}</div>
                </div>

                <div className="flex flex-wrap gap-2">
                  {line.trailerTypes.map((trailerType) => {
                    return (
                      <div
                        className="flex flex-col items-center justify-center rounded bg-neutral-subtle px-2 py-1"
                        key={`${line.id}-${trailerType}`}
                      >
                        <TrailerTypeIcon type={trailerType} variant="small" />
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </>
  );
};
